import {
  getRetailerConfig,
  getUserStoresForRetailer,
  normalizeStoreId,
  type RetailerType,
} from "../config/retailers";
import { getRetailerStoreLimit } from "./retailerStoreLimits";

export const PHYSICAL_STORE_RETAILERS: RetailerType[] = [
  "walmart",
  "home_depot",
  "target",
  "dollar_general",
  "bestbuy",
  "costco",
  "lowes",
  "tractor_supply",
  "northern_tool",
  "samsclub",
];

export function normalizeWalmartStoreId(storeId: string): string {
  let id = String(storeId).trim();
  if (!id) return id;
  id = id.replace(/-CRAZY-DEALS$/i, "");
  if (/^\d+$/.test(id)) {
    return id.replace(/^0+/, "") || "0";
  }
  return id;
}

export function activeStoreRetailers(user: {
  retailers?: string[];
  storeIds?: string[];
  homeDepotStoreIds?: string[];
  targetStoreIds?: string[];
  dollarGeneralStoreIds?: string[];
  bestbuyStoreIds?: string[];
  costcoStoreIds?: string[];
  lowesStoreIds?: string[];
  tractorSupplyStoreIds?: string[];
  northernToolStoreIds?: string[];
  samsClubStoreIds?: string[];
  aceHardwareStoreIds?: string[];
}): RetailerType[] {
  const userRetailers = user.retailers || [];
  return PHYSICAL_STORE_RETAILERS.filter((retailer) => {
    if (getUserStoresForRetailer(user as any, retailer).length > 0) {
      return true;
    }
    return getRetailerStoreLimit(userRetailers, retailer) > 0;
  });
}

export function catalogIdFromStore(
  store: unknown,
  retailer: RetailerType
): string | null {
  if (!store || typeof store !== "object") return null;
  const s = store as Record<string, unknown>;
  const field = getRetailerConfig(retailer).storeData.storeIdField;
  let raw: unknown = s[field];
  if (retailer === "home_depot") raw = s.store_number ?? s.Id ?? raw;
  if (retailer === "dollar_general") raw = s.storeNumber ?? raw;
  if (retailer === "target" || retailer === "lowes") raw = s["Store No."] ?? raw;
  if (retailer === "samsclub") raw = s.store_id ?? raw;
  if (retailer === "costco" && raw != null) {
    return normalizeStoreId(`${raw}-wh`, retailer);
  }
  if (raw == null || raw === "") return null;
  const id = String(raw);
  if (retailer === "walmart") {
    return normalizeWalmartStoreId(id);
  }
  return normalizeStoreId(id, retailer);
}

export function buildCatalogIdSet(
  stores: unknown[] | null | undefined,
  retailer: RetailerType
): Set<string> {
  const set = new Set<string>();
  if (!Array.isArray(stores)) return set;
  for (const store of stores) {
    const id = catalogIdFromStore(store, retailer);
    if (id) set.add(id);
  }
  return set;
}

export function getInvalidStoreIds(
  userStoreIds: string[],
  catalog: Set<string>,
  retailer: RetailerType
): string[] {
  return userStoreIds.filter((id) => {
    const normalized =
      retailer === "walmart" ? normalizeWalmartStoreId(id) : normalizeStoreId(id, retailer);
    return !catalog.has(normalized);
  });
}

export function findInvalidStoresByRetailer(
  user: {
    retailers?: string[];
    storeIds?: string[];
    homeDepotStoreIds?: string[];
    targetStoreIds?: string[];
    dollarGeneralStoreIds?: string[];
    bestbuyStoreIds?: string[];
    costcoStoreIds?: string[];
    lowesStoreIds?: string[];
    tractorSupplyStoreIds?: string[];
    northernToolStoreIds?: string[];
    samsClubStoreIds?: string[];
    aceHardwareStoreIds?: string[];
  },
  catalogs: Partial<Record<RetailerType, unknown[]>>
): Partial<Record<RetailerType, string[]>> {
  const result: Partial<Record<RetailerType, string[]>> = {};
  for (const retailer of activeStoreRetailers(user)) {
    const catalog = buildCatalogIdSet(catalogs[retailer], retailer);
    if (catalog.size === 0) continue;
    const saved = getUserStoresForRetailer(user as any, retailer);
    const invalid = getInvalidStoreIds(saved, catalog, retailer);
    if (invalid.length > 0) result[retailer] = invalid;
  }
  return result;
}

export function invalidStoresFingerprint(
  invalidByRetailer: Partial<Record<RetailerType, string[]>>
): string {
  return Object.entries(invalidByRetailer)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([retailer, ids]) => `${retailer}:${[...(ids || [])].sort().join(",")}`)
    .join("|");
}

export function retailerDisplayLabel(retailer: RetailerType): string {
  return getRetailerConfig(retailer).displayName;
}
